import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import { useGetProjectsByWorkerQuery } from "@/features/projects";
import {
  useGetTimesheetAllocationsQuery,
  useBulkUpdateWorkerAllocationsMutation,
} from "../api";
import type { TimesheetEntryResponse, TimesheetAllocationResponse } from "../types";
import type { PromptState } from "./useTimesheetModals";

interface ProjectOption {
  id: number;
  name: string;
}

export interface WorkerAllocationSummary {
  workerId: number;
  workerName: string;
  workHours: number;
  totalAllocated: number;
  allocations: TimesheetAllocationResponse[];
}

interface UseTimesheetAllocationsProps {
  timesheetId: number;
  entries: TimesheetEntryResponse[];
  isDraft: boolean;
  canEdit: boolean;
  setPrompt: (prompt: PromptState | null) => void;
}

export function useTimesheetAllocations({
  timesheetId,
  entries,
  isDraft,
  canEdit,
  setPrompt,
}: UseTimesheetAllocationsProps) {
  const { t } = useTranslation("TimesheetsPage");

  const {
    data: allocations,
    isLoading: isLoadingAllocations,
  } = useGetTimesheetAllocationsQuery(timesheetId, {
    skip: !timesheetId,
  });

  const [bulkUpdate] = useBulkUpdateWorkerAllocationsMutation();
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | null>(null);

  const { data: workerProjects, isLoading: isLoadingWorkerProjects, isFetching: isFetchingWorkerProjects } = useGetProjectsByWorkerQuery(
    selectedWorkerId!,
    { skip: selectedWorkerId === null },
  );

  const allAllocationsMatched = useMemo(() => {
    if (!entries || entries.length === 0) return true;
    if (!allocations || allocations.length === 0) return false;

    const workerMap = new Map<number, { workHours: number; totalAllocated: number }>();
    for (const entry of entries) {
      workerMap.set(entry.worker.id, { workHours: entry.work_hours, totalAllocated: 0 });
    }
    for (const alloc of allocations) {
      const worker = workerMap.get(alloc.worker_id);
      if (worker) {
        worker.workHours = alloc.worker_work_hours;
        worker.totalAllocated = parseFloat(alloc.worker_allocated_total) || 0;
      }
    }
    return Array.from(workerMap.values()).every((w) => w.totalAllocated === w.workHours && w.workHours > 0);
  }, [entries, allocations]);

  const workers: WorkerAllocationSummary[] = useMemo(() => {
    if (!entries || entries.length === 0) return [];

    const workerMap = new Map<number, WorkerAllocationSummary>();

    for (const entry of entries) {
      workerMap.set(entry.worker.id, {
        workerId: entry.worker.id,
        workerName: entry.worker.full_name,
        workHours: entry.work_hours,
        totalAllocated: 0,
        allocations: [],
      });
    }

    if (allocations) {
      for (const alloc of allocations) {
        const worker = workerMap.get(alloc.worker_id);
        if (worker) {
          worker.allocations.push(alloc);
        }
      }

      for (const worker of workerMap.values()) {
        if (worker.allocations.length > 0) {
          const firstAlloc = worker.allocations[0];
          worker.workHours = firstAlloc.worker_work_hours;
          worker.totalAllocated = parseFloat(firstAlloc.worker_allocated_total) || 0;
        }
      }
    }

    return Array.from(workerMap.values());
  }, [entries, allocations]);

  const selectedWorker = useMemo(() => {
    if (selectedWorkerId === null) return null;
    return workers.find((w) => w.workerId === selectedWorkerId) || null;
  }, [selectedWorkerId, workers]);

  const workerProjectOptions: ProjectOption[] = useMemo(() => {
    if (!workerProjects) return [];
    return workerProjects;
  }, [workerProjects]);

  function openWorkerModal(workerId: number) {
    setSelectedWorkerId(workerId);
  }

  function closeWorkerModal() {
    setSelectedWorkerId(null);
  }

  async function handleBulkSave(
    workerId: number,
    allocationItems: Array<{ project_id: number; hours: string }>,
  ) {
    if (isBulkSaving) return;
    try {
      setIsBulkSaving(true);
      const filtered = allocationItems.filter((a) => parseFloat(a.hours) > 0);
      await bulkUpdate({
        timesheetId,
        workerId,
        body: { allocations: filtered },
      }).unwrap();
      closeWorkerModal();
      setPrompt({
        title: t("allocations.messages.createSuccessTitle"),
        text: t("allocations.messages.createSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "allocations.messages.createErrorText", t),
        variant: "error",
      });
    } finally {
      setIsBulkSaving(false);
    }
  }

  return {
    workers,
    allAllocationsMatched,
    selectedWorker,
    openWorkerModal,
    closeWorkerModal,
    workerProjectOptions,
    isLoadingAllocations,
    isLoadingWorkerProjects: isLoadingWorkerProjects || isFetchingWorkerProjects,
    isBulkSaving,
    handleBulkSave,
    isDraft,
    canEdit,
    t,
  };
}

export type UseTimesheetAllocationsReturn = ReturnType<typeof useTimesheetAllocations>;
